/**
 * Admin · Judge calls — the AI processing of one candidacy, step by step, with comparison reruns
 * (GET/POST /admin/candidacies/{id}/ai-runs, GET /admin/ai-step-runs/{id}, POST …/promote —
 * adminAiStepRuns.ts, migration 0093; raw judge payloads from GET /admin/judge-calls).
 *
 *   STEPS    one compact card per real AI step (packages/shared/src/aiSteps.ts): what is live, on
 *            which model, at what usage and cost. Tick steps and "Rerun selected", or rerun one.
 *            A step whose input does not exist is disabled with the reason; unticking a step can
 *            strand the next one, which is unticked with it.
 *   MODELS   a model step can be rerun on any registry model its code can call (ai_models) — the
 *            list comes from the server, never from this file. Speech has no model choice.
 *   HISTORY  one row per run, one column per step, each cell a mini-card; "Copy all" puts the
 *            whole table on the clipboard as Markdown for an AI chat.
 *   DETAILS  the eye opens a step's full record: prompt, output, every provider call, cost.
 *
 * A RERUN NEVER CHANGES THE LIVE RESULT. It is a comparison run; "Make authoritative" is the
 * separate, owner-only, audited act, and the server refuses it once a human has decided.
 * Computed scores (resume overall, interview composite/recommendation) are shown, never rerun —
 * they are arithmetic, not model calls.
 */
import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AI_STEPS,
  aiRunsMarkdown,
  aiStepAvailability,
  formatDuration,
  formatTokens,
  formatUsd,
  pruneAiStepSelection,
  type AiRunRow,
  type AiStepAvailability,
  type AiStepCell,
  type AiStepFacts,
  type AiStepKey,
} from "@talenthop/shared";
import { api, ApiError } from "../../lib/api.js";
import { useAuth } from "../../lib/AuthContext.js";
import { Check, Copy, Eye, RefreshCw } from "../../components/icons.js";
import { Modal } from "./adminUi.js";
import { EvaluationList, type EvaluationDetail } from "./JudgeEvaluations.js";

// ── API shapes ────────────────────────────────────────────────────────────────

interface JudgeCall {
  id: string;
  sessionId: string | null;
  provider: string;
  model: string;
  request: unknown;
  response: unknown;
  ok: boolean;
  error: string | null;
  latencyMs: number | null;
  createdAt: string;
  stepRunId?: string | null;
  evaluationId?: string | null;
  competencyId?: string | null;
}

/** One row of the candidacy's evaluation history (0099) — failed runs included, never current. */
interface EvaluationHistoryEntry {
  id: string;
  status: "succeeded" | "failed";
  trigger: string;
  isCurrent: boolean;
  assessmentSetVersion: number;
  capability: number | null;
  coverage: number | null;
  reason: string | null;
  error: string | null;
  triggeredBy: string | null;
  judgeModel: string | null;
  recommendation: string | null;
  createdAt: string;
}

interface OrgFlags {
  rescoreEnabled: boolean;
  customPromptEnabled: boolean;
  copyQuestionsEnabled: boolean;
}

interface JudgeCallsResponse {
  candidacyState: string;
  orgId: string;
  orgName: string;
  flags?: OrgFlags;
  evaluations: EvaluationHistoryEntry[];
  evaluationDetails?: EvaluationDetail[];
  calls: JudgeCall[];
}

interface ModelOption {
  key: string;
  displayName: string;
  provider: string;
  model: string;
  inputUsdPerMTok: number | null;
  outputUsdPerMTok: number | null;
}

interface StepView {
  key: AiStepKey;
  label: string;
  group: "resume" | "interview";
  kind: "model" | "speech";
  service: string | null;
  promotable: "self" | "with_evaluation" | "never";
  description: string;
  currentRouting: { source: string; label: string } | null;
  models: ModelOption[];
}

interface CallView {
  id: string;
  operation: string;
  feature: string;
  provider: string;
  model: string;
  modelVersion: string | null;
  status: string;
  error: string | null;
  attempt: number;
  inputTokens: number | null;
  outputTokens: number | null;
  audioSeconds: number | null;
  usageUnit: string | null;
  usageQuantity: number | null;
  unitPriceUsd: number | null;
  priceRef: string | null;
  costInr: number | null;
  costUsd: number | null;
  routeRank: number | null;
  latencyMs: number | null;
  createdAt: string;
}

interface PipelineResponse {
  candidacyId: string;
  candidacyState: string;
  orgName: string;
  decided: boolean;
  facts: AiStepFacts;
  steps: StepView[];
  computed: Record<string, string | number | null>;
  runs: AiRunRow[];
  originalCalls: CallView[];
  pending: boolean;
}

interface StepRunDetail {
  id: string;
  runNo: number;
  candidacyState: string;
  step: AiStepKey;
  stepLabel: string;
  kind: "model" | "speech";
  promotable: "self" | "with_evaluation" | "never";
  status: string;
  requestedModelKey: string | null;
  provider: string | null;
  model: string | null;
  modelVersion: string | null;
  inputRef: unknown;
  promptKey: string | null;
  promptSha256: string | null;
  promptText: string | null;
  output: unknown;
  summary: Record<string, unknown> | null;
  error: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  processingMs: number | null;
  isAuthoritative: boolean;
  promotion: string | null;
  promotionError: string | null;
  costInr: number;
  costUsd: number | null;
  calls: CallView[];
  judgeCalls: JudgeCall[] | null;
}

type Detail = { kind: "run"; stepRunId: string } | { kind: "original"; step: AiStepKey };

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function pretty(value: unknown): string {
  if (value === null || value === undefined) return "(none)";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/** The server's own reason (its JSON `message`) — `ApiError.message` is only "Request failed: N". */
function errText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const m = (err.body as { message?: unknown } | undefined)?.message;
    if (typeof m === "string" && m.trim()) return m;
    if (err.status === 403) return "Only the owner can do that.";
  }
  return fallback;
}

/** The cell the recruiter's report uses now for a step, and which run it came from. */
function liveCell(runs: readonly AiRunRow[], step: AiStepKey): { cell: AiStepCell; run: AiRunRow } | null {
  for (const run of runs) {
    const cell = run.steps[step];
    if (cell?.isAuthoritative) return { cell, run };
  }
  return null;
}

/** Who served a cell — or, before any model answered, that none has. */
function cellWho(c: AiStepCell): string {
  return c.modelLabel ?? c.model ?? (c.status === "queued" || c.status === "running" ? "Current routing" : "No model call");
}

function usageLine(kind: "model" | "speech", c: AiStepCell): string {
  if (kind === "speech") {
    return [c.audioSeconds != null ? formatDuration(c.audioSeconds) : null, c.characters != null ? `${formatTokens(c.characters)} chars` : null]
      .filter(Boolean)
      .join(" · ") || "—";
  }
  return `In ${formatTokens(c.inputTokens)} · Out ${formatTokens(c.outputTokens)}`;
}

const STATUS_PILL: Record<string, { cls: string; text: string }> = {
  succeeded: { cls: "pill v", text: "Completed" },
  failed: { cls: "pill r", text: "Failed" },
  queued: { cls: "pill n", text: "Queued" },
  running: { cls: "pill pv", text: "Running" },
};

// ── page ─────────────────────────────────────────────────────────────────────

export function JudgeCalls(): ReactNode {
  const [candidacyId, setCandidacyId] = useState("");
  const cleanId = UUID_RE.test(candidacyId.trim()) ? candidacyId.trim().toLowerCase() : "";

  const judge = useQuery({
    queryKey: ["admin-judge-calls", cleanId],
    queryFn: () => api.get<JudgeCallsResponse>(`/admin/judge-calls?candidacyId=${cleanId}`),
    enabled: cleanId !== "",
  });
  const pipeline = useQuery({
    queryKey: ["admin-ai-runs", cleanId],
    queryFn: () => api.get<PipelineResponse>(`/admin/candidacies/${cleanId}/ai-runs`),
    enabled: cleanId !== "",
    // Worker steps and evaluation promotions finish asynchronously — poll only while one is open.
    refetchInterval: (q) => (q.state.data?.pending ? 3000 : false),
  });

  return (
    <section>
      <div className="page-h">
        <div>
          <h1>Judge calls</h1>
          <div className="sub">
            Every AI step of one candidate's processing — what is live, on which model, at what cost — with comparison reruns.
            A rerun never changes the live result until you make it authoritative.
          </div>
          <input
            className="rq-in"
            aria-label="Candidacy ID"
            style={{ maxWidth: 360, marginTop: 12, fontFamily: "var(--mono)" }}
            placeholder="Candidacy ID (uuid)"
            value={candidacyId}
            onChange={(e) => setCandidacyId(e.target.value)}
          />
          {candidacyId !== "" && (
            <button type="button" className="btn sm ghost" style={{ marginLeft: 8 }} onClick={() => setCandidacyId("")}>
              ← Recent candidacies
            </button>
          )}
        </div>
        <span className="pill n">Owner-only · candidate transcript in details</span>
      </div>

      {candidacyId.trim() !== "" && cleanId === "" && (
        <div className="banner" style={{ marginBottom: 16 }}>
          <span>That doesn't look like a candidacy UUID.</span>
        </div>
      )}
      {cleanId !== "" && (pipeline.isLoading || judge.isLoading) && <div className="card pad mut sm">Loading…</div>}
      {cleanId !== "" && (pipeline.isError || judge.isError) && (
        <div className="banner crit">
          <span>{errText(pipeline.error ?? judge.error, "Couldn't load this candidacy. Please retry in a moment.")}</span>
        </div>
      )}

      {candidacyId.trim() === "" && <RecentCandidacies onPick={setCandidacyId} />}

      {cleanId !== "" && judge.data && (
        <OrgFlagsSummary
          orgId={judge.data.orgId}
          orgName={judge.data.orgName}
          flags={judge.data.flags ?? { rescoreEnabled: false, customPromptEnabled: false, copyQuestionsEnabled: true }}
        />
      )}

      {cleanId !== "" && pipeline.data && (
        <Pipeline data={pipeline.data} rawJudgeCalls={judge.data?.calls ?? []} scores={judge.data?.evaluations ?? []} evaluationDetails={judge.data?.evaluationDetails ?? []} />
      )}
    </section>
  );
}

interface RecentCandidacy {
  id: string;
  state: string;
  orgName: string;
  requisitionTitle: string;
  lastActivityAt: string;
}

/** Latest candidacies with AI activity — click one instead of pasting a UUID. */
function RecentCandidacies({ onPick }: { onPick: (id: string) => void }): ReactNode {
  const recent = useQuery({
    queryKey: ["admin-judge-calls-recent"],
    queryFn: () => api.get<{ candidacies: RecentCandidacy[] }>("/admin/judge-calls/recent"),
  });
  const rows = recent.data?.candidacies ?? [];
  // An API that predates this endpoint (web deployed ahead of it) → just the paste-an-ID box.
  if (recent.error instanceof ApiError && recent.error.status === 404) return null;
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-h">
        <h3>Recent candidacies</h3>
        <span className="tag">latest AI activity first · click to open</span>
      </div>
      {recent.isLoading && <div className="card-b mut sm">Loading…</div>}
      {recent.isError && <div className="card-b mut sm">{errText(recent.error, "Couldn't load recent candidacies.")}</div>}
      {recent.data && rows.length === 0 && <div className="card-b mut sm">No candidacies with AI activity yet.</div>}
      {rows.length > 0 && (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr><th>Last activity</th><th>Org</th><th>Requisition</th><th>State</th><th>Candidacy</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} onClick={() => onPick(r.id)} style={{ cursor: "pointer" }}>
                  <td className="mut sm">{new Date(r.lastActivityAt).toLocaleString()}</td>
                  <td>{r.orgName}</td>
                  <td>{r.requisitionTitle}</td>
                  <td><span className="pill n">{r.state.replace(/_/g, " ")}</span></td>
                  <td className="mut sm" style={{ fontFamily: "var(--mono)" }}>{r.id.slice(0, 8)}…</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Pipeline({ data, rawJudgeCalls, scores, evaluationDetails }: { data: PipelineResponse; rawJudgeCalls: JudgeCall[]; scores: EvaluationHistoryEntry[]; evaluationDetails: EvaluationDetail[] }): ReactNode {
  const qc = useQueryClient();
  const { me } = useAuth();
  const isOwner = me?.role === "owner";
  const [selected, setSelected] = useState<AiStepKey[]>([]);
  const [modelFor, setModelFor] = useState<Partial<Record<AiStepKey, string>>>({});
  const [reason, setReason] = useState("");
  const [detail, setDetail] = useState<Detail | null>(null);
  const [promote, setPromote] = useState<AiRunRow | null>(null);

  const availability = useMemo(() => aiStepAvailability(data.facts, selected), [data.facts, selected]);
  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ["admin-ai-runs", data.candidacyId] });
    void qc.invalidateQueries({ queryKey: ["admin-judge-calls", data.candidacyId] });
  };

  const rerun = useMutation({
    mutationFn: (steps: AiStepKey[]) =>
      api.post<{ runNo: number; queuedForWorker: AiStepKey[] }>(`/admin/candidacies/${data.candidacyId}/ai-runs`, {
        steps: steps.map((s) => ({ step: s, modelKey: modelFor[s] || null })),
        reason: reason.trim() || null,
      }),
    onSuccess: () => {
      setSelected([]);
      setReason("");
      invalidate();
    },
  });

  const toggle = (step: AiStepKey): void =>
    setSelected((cur) => pruneAiStepSelection(data.facts, cur.includes(step) ? cur.filter((s) => s !== step) : [...cur, step]));

  return (
    <>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-h">
          <h3>Pipeline</h3>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {Object.entries(data.computed).map(([k, v]) => (
              <span key={k} className="tag" title="Computed from the step outputs — not a model call, so never rerun">
                {k}: {v == null ? "—" : typeof v === "string" ? v.slice(0, 48) + (v.length > 48 ? "…" : "") : v}
              </span>
            ))}
            <span className={data.decided ? "pill r" : "pill n"}>{data.candidacyState}</span>
          </div>
        </div>
        <div className="card-b">
          {data.decided && (
            <div className="banner info" style={{ marginBottom: 12 }}>
              <span>
                A human has decided on this candidacy. You can still run comparisons, but none can be made authoritative.
              </span>
            </div>
          )}
          <div className="jc-steps-wrap">
          <div className="jc-steps">
            {data.steps.map((s) => (
              <StepCard
                key={s.key}
                step={s}
                runs={data.runs}
                avail={availability[s.key]}
                soloAvail={aiStepAvailability(data.facts, [s.key])[s.key]}
                checked={selected.includes(s.key)}
                onToggle={() => toggle(s.key)}
                model={modelFor[s.key] ?? ""}
                onModel={(m) => setModelFor((cur) => ({ ...cur, [s.key]: m }))}
                canRun={isOwner && !rerun.isPending}
                onRerun={() => rerun.mutate([s.key])}
                onDetails={(d) => setDetail(d)}
              />
            ))}
          </div>
          </div>
          <div className="jc-toolbar">
            <input
              className="rq-in"
              style={{ flex: "1 1 260px", maxWidth: 420 }}
              placeholder="Note for this run (optional, audited) — e.g. 'Haiku vs Flash on evaluation'"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={!isOwner}
            />
            <button
              className="btn primary sm"
              disabled={!isOwner || selected.length === 0 || rerun.isPending}
              onClick={() => rerun.mutate(selected)}
            >
              <RefreshCw /> {rerun.isPending ? "Running…" : `Rerun selected (${selected.length})`}
            </button>
            {rerun.isError && <span className="sm" style={{ color: "var(--crit)" }}>{errText(rerun.error, "Couldn't start the rerun.")}</span>}
            {rerun.isSuccess && (
              <span className="sm mut">
                Run #{rerun.data.runNo} recorded
                {rerun.data.queuedForWorker.length > 0 ? " — interview steps are queued for the worker; this page refreshes itself" : ""}.
              </span>
            )}
            {!isOwner && <span className="sm mut">Only the owner can rerun.</span>}
          </div>
        </div>
      </div>

      <History data={data} onDetails={setDetail} onPromote={isOwner && !data.decided ? setPromote : null} />

      {/* P1-8: evaluation-centric — rubric/version, models (fallbacks), validation, per-competency verdicts, evidence and raw calls. */}
      <EvaluationList evaluations={evaluationDetails} calls={rawJudgeCalls} />

      {detail && (
        <DetailModal
          detail={detail}
          data={data}
          rawJudgeCalls={rawJudgeCalls}
          canPromote={isOwner && !data.decided}
          onClose={() => setDetail(null)}
          onPromoted={invalidate}
        />
      )}
      {promote && <PromoteDialog run={promote} data={data} onClose={() => setPromote(null)} onDone={invalidate} />}
    </>
  );
}

// ── one step card ────────────────────────────────────────────────────────────

function StepCard({
  step,
  runs,
  avail,
  soloAvail,
  checked,
  onToggle,
  model,
  onModel,
  canRun,
  onRerun,
  onDetails,
}: {
  step: StepView;
  runs: AiRunRow[];
  avail: AiStepAvailability;
  soloAvail: AiStepAvailability;
  checked: boolean;
  onToggle: () => void;
  model: string;
  onModel: (key: string) => void;
  canRun: boolean;
  onRerun: () => void;
  onDetails: (d: Detail) => void;
}): ReactNode {
  const live = liveCell(runs, step.key);
  const latest = [...runs].reverse().find((r) => r.steps[step.key]);
  const shown = live ?? (latest ? { cell: latest.steps[step.key]!, run: latest } : null);
  const status = shown ? STATUS_PILL[shown.cell.status] : null;
  const detailOf = (): Detail | null =>
    shown ? (shown.cell.stepRunId ? { kind: "run", stepRunId: shown.cell.stepRunId } : { kind: "original", step: step.key }) : null;

  return (
    <div className={`jc-step${avail.available ? "" : " off"}${checked ? " on" : ""}`}>
      <div className="jc-step-h">
        <label style={{ display: "flex", gap: 6, alignItems: "center", minWidth: 0, cursor: avail.available ? "pointer" : "not-allowed" }}>
          <input type="checkbox" checked={checked} disabled={!avail.available} onChange={onToggle} />
          <span className="jc-step-name" title={step.description}>{step.label}</span>
        </label>
        <button
          type="button"
          className="btn sm ghost jc-icon"
          aria-label="Details"
          data-tip="Details"
          disabled={!shown}
          onClick={() => {
            const d = detailOf();
            if (d) onDetails(d);
          }}
        >
          <Eye />
        </button>
      </div>

      <div className="jc-step-b">
        {shown ? (
          <>
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
              {status && <span className={status.cls}>{status.text}</span>}
              <span className="mut sm">{live ? `Live · ${shown.run.label}` : `Latest · ${shown.run.label} · not live`}</span>
            </div>
            <div className="jc-model" title={shown.cell.model ?? ""}>{shown.cell.modelLabel ?? shown.cell.model ?? "—"}</div>
            <div className="mut sm tnum">{usageLine(step.kind, shown.cell)}</div>
            <div className="sm tnum">Cost {formatUsd(shown.cell.costUsd)}</div>
          </>
        ) : (
          <div className="mut sm">No calls recorded for this step.</div>
        )}
        <div className={`sm ${avail.available ? "mut" : ""}`} style={avail.available ? undefined : { color: "var(--warn)" }}>
          {avail.note}
        </div>
      </div>

      <div className="jc-step-f">
        {step.kind === "model" ? (
          <select
            className="input jc-select"
            value={model}
            onChange={(e) => onModel(e.target.value)}
            disabled={!avail.available && !soloAvail.available}
            title="Model for the next rerun of this step"
          >
            <option value="" title={step.currentRouting?.label ?? "code default"}>
              Current routing{step.currentRouting ? ` — ${step.currentRouting.label}` : ""}
            </option>
            {step.models.map((m) => (
              <option key={m.key} value={m.key}>
                {m.displayName}
                {m.inputUsdPerMTok != null ? ` · $${m.inputUsdPerMTok}/$${m.outputUsdPerMTok} per 1M` : ""}
              </option>
            ))}
          </select>
        ) : (
          <span className="mut sm jc-select" title="A speech service, not a language model — no model choice">
            Azure Speech · fixed
          </span>
        )}
        <button
          type="button"
          className="btn sm"
          disabled={!canRun || !soloAvail.available}
          title={soloAvail.available ? `Rerun only ${step.label}` : soloAvail.note}
          onClick={onRerun}
        >
          <RefreshCw /> Rerun
        </button>
      </div>
    </div>
  );
}

// ── history ──────────────────────────────────────────────────────────────────

function History({
  data,
  onDetails,
  onPromote,
}: {
  data: PipelineResponse;
  onDetails: (d: Detail) => void;
  onPromote: ((run: AiRunRow) => void) | null;
}): ReactNode {
  // Newest run first — the one you just queued is the one you want to look at.
  const runs = [...data.runs].reverse();
  const markdown = (rs: readonly AiRunRow[]): string =>
    aiRunsMarkdown({
      candidacyId: data.candidacyId,
      candidacyState: data.candidacyState,
      orgName: data.orgName,
      runs: rs,
      computed: data.computed,
    });

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-h">
        <h3>Comparison history</h3>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span className="tag">one row per run · LIVE = what the report uses</span>
          <CopyIcon
            className="btn sm primary"
            text={() => markdown(runs)}
            disabled={runs.length === 0}
            title="Copy the whole table as Markdown — paste it into an AI chat to compare runs"
          />
        </div>
      </div>
      {data.runs.length === 0 ? (
        <div className="card-b mut sm">No AI calls recorded for this candidacy yet, and no reruns.</div>
      ) : (
        <div className="tbl-wrap">
          <table className="tbl jc-hist">
            <thead>
              <tr>
                <th>Run</th>
                {AI_STEPS.map((s) => <th key={s.key}>{s.label}</th>)}
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => {
                const promotable = AI_STEPS.filter(
                  (s) => s.promotable === "self" && run.steps[s.key]?.status === "succeeded" && !run.steps[s.key]?.isAuthoritative && run.steps[s.key]?.stepRunId,
                );
                return (
                  <tr key={run.runId ?? "original"}>
                    <td style={{ whiteSpace: "nowrap", verticalAlign: "top" }}>
                      <div style={{ fontWeight: 650, display: "flex", alignItems: "center", gap: 4 }}>
                        {run.label}
                        <CopyIcon className="btn sm ghost jc-row-copy" text={() => markdown([run])} title="Copy this run as Markdown" />
                      </div>
                      {run.parentRunNo != null && <div className="mut sm">after #{run.parentRunNo}</div>}
                      {run.createdAt && <div className="mut sm">{new Date(run.createdAt).toLocaleString()}</div>}
                      {run.reason && <div className="mut sm" style={{ maxWidth: 160, whiteSpace: "normal" }}>{run.reason}</div>}
                      {onPromote && promotable.length > 0 && (
                        <button className="btn sm ghost" style={{ marginTop: 4 }} onClick={() => onPromote(run)}>Make authoritative…</button>
                      )}
                    </td>
                    {AI_STEPS.map((s) => (
                      <td key={s.key} style={{ verticalAlign: "top" }}>
                        <MiniCell
                          kind={s.kind}
                          cell={run.steps[s.key]}
                          onOpen={() => {
                            const c = run.steps[s.key];
                            if (c) onDetails(c.stepRunId ? { kind: "run", stepRunId: c.stepRunId } : { kind: "original", step: s.key });
                          }}
                        />
                      </td>
                    ))}
                    <td className="tnum" style={{ verticalAlign: "top", whiteSpace: "nowrap" }}>
                      <div style={{ fontWeight: 650 }}>{formatUsd(run.totalCostUsd)}</div>
                      <div className="mut sm">₹{run.totalCostInr.toFixed(2)}</div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MiniCell({ kind, cell, onOpen }: { kind: "model" | "speech"; cell: AiStepCell | undefined; onOpen: () => void }): ReactNode {
  if (!cell) {
    return (
      <div className="jc-cell empty">
        <div>—</div>
        <div className="mut sm">Not run</div>
      </div>
    );
  }
  const st = STATUS_PILL[cell.status];
  const s = cell.summary ?? {};
  return (
    <button type="button" className={`jc-cell${cell.isAuthoritative ? " live" : ""}`} onClick={onOpen} title={`${cellWho(cell)} — details`}>
      <div className="jc-model">{cellWho(cell)}</div>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
        <span className={st?.cls ?? "pill n"}>{st?.text ?? cell.status}</span>
        {cell.isAuthoritative && <span className="pill v">Live</span>}
        {cell.promotion === "pending" && <span className="pill pv">Promoting</span>}
      </div>
      <div className="mut sm tnum">{usageLine(kind, cell)}</div>
      <div className="sm tnum">{formatUsd(cell.costUsd)}{cell.calls > 1 ? <span className="mut"> · {cell.calls} calls</span> : null}</div>
      {cell.fallbackCalls > 0 && <div className="sm" style={{ color: "var(--warn)" }}>{cell.fallbackCalls} via fallback</div>}
      {typeof s.overall === "number" && <div className="sm">Overall {s.overall}</div>}
      {"capability" in s && <div className="sm">Capability {typeof s.capability === "number" ? s.capability : "—"}</div>}
      {typeof s.coverage === "number" && (
        <div className="mut sm">
          Coverage {Math.round(s.coverage * 100)}%
          {typeof s.evidenced === "number" && typeof s.competencies === "number" ? ` · ${s.evidenced}/${s.competencies} evidenced` : ""}
          {typeof s.assessmentSetVersion === "number" ? ` · v${s.assessmentSetVersion}` : ""}
        </div>
      )}
      {cell.error && <div className="sm jc-err" title={cell.error}>{cell.error}</div>}
    </button>
  );
}

// ── details ──────────────────────────────────────────────────────────────────

function DetailModal({
  detail,
  data,
  rawJudgeCalls,
  canPromote,
  onClose,
  onPromoted,
}: {
  detail: Detail;
  data: PipelineResponse;
  rawJudgeCalls: JudgeCall[];
  canPromote: boolean;
  onClose: () => void;
  onPromoted: () => void;
}): ReactNode {
  const run = useQuery({
    queryKey: ["admin-ai-step-run", detail.kind === "run" ? detail.stepRunId : ""],
    queryFn: () => api.get<StepRunDetail>(`/admin/ai-step-runs/${(detail as { stepRunId: string }).stepRunId}`),
    enabled: detail.kind === "run",
  });
  const stepKey = detail.kind === "run" ? run.data?.step : detail.step;
  const def = data.steps.find((s) => s.key === stepKey);

  if (detail.kind === "original") {
    const calls = data.originalCalls.filter((c) => c.operation === detail.step);
    const cell = data.runs.find((r) => r.runId === null)?.steps[detail.step];
    return (
      <Modal title={`${def?.label ?? detail.step} · Original processing`} onClose={onClose} maxWidth={920}>
        {cell && <Facts rows={cellFacts(def?.kind ?? "model", cell, { run: "Original", authoritative: cell.isAuthoritative })} />}
        <div className="mut sm" style={{ margin: "8px 0" }}>
          Every call of this step not made by a comparison run — first processing and any earlier re-score. Prompts were not stored for
          these calls{detail.step === "judge.score" ? ", except the judge's raw requests below" : ""}.
        </div>
        <CallsTable calls={calls} />
        {detail.step === "judge.score" && <RawJudgeCalls calls={rawJudgeCalls.filter((c) => !c.stepRunId)} />}
      </Modal>
    );
  }

  const d = run.data;
  return (
    <Modal title={d ? `${d.stepLabel} · Run #${d.runNo}` : "Step run"} onClose={onClose} maxWidth={920}>
      {run.isLoading && <div className="mut sm">Loading…</div>}
      {run.isError && <div className="banner crit"><span>{errText(run.error, "Couldn't load this step run.")}</span></div>}
      {d && (
        <>
          <Facts
            rows={[
              ["Step", d.stepLabel],
              ["Run", `#${d.runNo}`],
              ["Status", d.status + (d.error ? ` — ${d.error}` : "")],
              ["Authoritative", d.isAuthoritative ? "Yes — live" : d.promotion === "pending" ? "Being made authoritative" : d.promotion === "failed" ? `No — promotion failed: ${d.promotionError ?? ""}` : "No"],
              ["Provider", d.provider ?? "—"],
              ["Model", `${d.model ?? "—"}${d.modelVersion && d.modelVersion !== d.model ? ` (${d.modelVersion})` : ""}`],
              ["Requested model", d.requestedModelKey ?? "Current routing"],
              ["Prompt", d.promptKey ?? "—"],
              ["Prompt version", d.promptSha256 ? `sha256:${d.promptSha256.slice(0, 12)}` : "—"],
              ...usageFacts(d.kind, d.calls),
              ["Cost", `${formatUsd(d.costUsd)} (₹${d.costInr.toFixed(4)})`],
              ["Processing time", d.processingMs != null ? formatDuration(d.processingMs / 1000) : "—"],
              ["Execution id", d.id],
              ["Started", d.startedAt ? new Date(d.startedAt).toLocaleString() : "—"],
              ["Completed", d.completedAt ? new Date(d.completedAt).toLocaleString() : "—"],
            ]}
          />
          {canPromote && d.promotable === "self" && d.status === "succeeded" && !d.isAuthoritative && d.promotion !== "pending" && d.output != null && (
            <PromoteInline stepRunIds={[d.id]} onDone={() => { onPromoted(); void run.refetch(); }} label={`Make this ${d.stepLabel.toLowerCase()} authoritative`} />
          )}
          {d.promotable === "with_evaluation" && <div className="mut sm" style={{ marginTop: 8 }}>Goes live only with an interview evaluation that used it.</div>}
          <CallsTable calls={d.calls} />
          <Collapsible title="Input" text={pretty(d.inputRef)} />
          {d.promptText && <Collapsible title={`Prompt (${d.promptKey ?? ""})`} text={d.promptText} />}
          <Collapsible title="Result" text={pretty(d.output)} />
          {d.judgeCalls && <RawJudgeCalls calls={d.judgeCalls} />}
        </>
      )}
    </Modal>
  );
}

function cellFacts(kind: "model" | "speech", c: AiStepCell, extra: { run: string; authoritative: boolean }): [string, string][] {
  return [
    ["Run", extra.run],
    ["Authoritative", extra.authoritative ? "Yes — live" : "No"],
    ["Provider", c.provider ?? "—"],
    ["Model", c.model ?? "—"],
    ...(kind === "speech"
      ? ([["Audio", formatDuration(c.audioSeconds)], ["Characters", formatTokens(c.characters)]] as [string, string][])
      : ([
          ["Input tokens", formatTokens(c.inputTokens)],
          ["Output tokens", formatTokens(c.outputTokens)],
          ["Total tokens", formatTokens(c.inputTokens != null || c.outputTokens != null ? (c.inputTokens ?? 0) + (c.outputTokens ?? 0) : null)],
        ] as [string, string][])),
    ["Calls", `${c.calls}${c.failedCalls ? ` (${c.failedCalls} failed)` : ""}${c.fallbackCalls ? `, ${c.fallbackCalls} via fallback` : ""}`],
    ["Cost", `${formatUsd(c.costUsd)} (₹${(c.costInr ?? 0).toFixed(4)})`],
  ];
}

function usageFacts(kind: "model" | "speech", calls: CallView[]): [string, string][] {
  const sum = (f: (c: CallView) => number | null): number | null => {
    const xs = calls.map(f).filter((x): x is number => x != null);
    return xs.length ? xs.reduce((a, b) => a + b, 0) : null;
  };
  if (kind === "speech") {
    const unit = calls.find((c) => c.unitPriceUsd != null);
    return [
      ["Service", calls[0]?.model ?? "fast_transcription"],
      ["Audio duration", formatDuration(sum((c) => c.audioSeconds))],
      ["Unit price", unit ? `$${unit.unitPriceUsd} per ${unit.usageUnit?.replace("_", " ")}` : "—"],
    ];
  }
  const inT = sum((c) => c.inputTokens);
  const outT = sum((c) => c.outputTokens);
  return [
    ["Input tokens", formatTokens(inT)],
    ["Output tokens", formatTokens(outT)],
    ["Total tokens", formatTokens(inT != null || outT != null ? (inT ?? 0) + (outT ?? 0) : null)],
    ["Calls", `${calls.length}${calls.some((c) => c.status !== "ok") ? ` (${calls.filter((c) => c.status !== "ok").length} failed)` : ""}`],
  ];
}

function Facts({ rows }: { rows: [string, string][] }): ReactNode {
  return (
    <div className="jc-facts">
      {rows.map(([k, v]) => (
        <div key={k}>
          <div className="mut sm">{k}</div>
          <div className="sm" style={{ wordBreak: "break-word" }}>{v}</div>
        </div>
      ))}
    </div>
  );
}

function CallsTable({ calls }: { calls: CallView[] }): ReactNode {
  if (calls.length === 0) return <div className="mut sm" style={{ marginTop: 8 }}>No provider calls recorded.</div>;
  return (
    <div className="tbl-wrap" style={{ marginTop: 10 }}>
      <table className="tbl">
        <thead>
          <tr><th>When</th><th>Model</th><th>Result</th><th>Usage</th><th>Price</th><th>Cost</th><th>Route</th><th>Latency</th></tr>
        </thead>
        <tbody>
          {calls.map((c) => (
            <tr key={c.id}>
              <td className="mut sm">{new Date(c.createdAt).toLocaleString()}</td>
              <td className="sm" style={{ fontFamily: "var(--mono)" }}>{c.provider}/{c.model}</td>
              <td className="sm" style={{ color: c.status === "ok" ? "var(--good)" : "var(--crit)" }} title={c.error ?? ""}>
                {c.status}{c.attempt > 1 ? ` · try ${c.attempt}` : ""}
              </td>
              <td className="sm tnum">
                {c.audioSeconds != null ? formatDuration(c.audioSeconds) : c.usageUnit === "character" ? `${formatTokens(c.usageQuantity)} chars` : `${formatTokens(c.inputTokens)} / ${formatTokens(c.outputTokens)}`}
              </td>
              <td className="mut sm" style={{ fontFamily: "var(--mono)", fontSize: 12 }}>{c.priceRef ?? "—"}</td>
              <td className="sm tnum">{formatUsd(c.costUsd)}</td>
              <td className="sm">{c.routeRank == null ? "default" : c.routeRank === 1 ? "primary" : `fallback ${c.routeRank - 1}`}</td>
              <td className="sm tnum">{c.latencyMs != null ? `${c.latencyMs}ms` : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Icon-only copy button: Copy icon, flips to a check on success (the hover label says what happened). */
function CopyIcon({
  text,
  title,
  className = "btn sm ghost",
  disabled,
}: {
  text: () => string;
  title: string;
  className?: string;
  disabled?: boolean;
}): ReactNode {
  const [state, setState] = useState<"" | "ok" | "fail">("");
  const label = state === "ok" ? "Copied" : state === "fail" ? "Copy failed" : title;
  return (
    <button
      type="button"
      className={className}
      disabled={disabled}
      data-tip={label}
      data-tip-pos="left"
      aria-label={label}
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(text()).then(
          () => setState("ok"),
          () => setState("fail"),
        );
        setTimeout(() => setState(""), 1500);
      }}
    >
      {state === "ok" ? <Check /> : <Copy />}
    </button>
  );
}

function Collapsible({ title, text }: { title: string; text: string }): ReactNode {
  return (
    <details style={{ marginTop: 10 }}>
      <summary className="sm" style={{ cursor: "pointer", fontWeight: 600 }}>{title}</summary>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <CopyIcon text={() => text} title="Copy" />
      </div>
      <pre className="jc-pre">{text}</pre>
    </details>
  );
}

function RawJudgeCalls({ calls }: { calls: JudgeCall[] }): ReactNode {
  if (calls.length === 0) return null;
  return (
    <div style={{ marginTop: 10 }}>
      <div className="sm" style={{ fontWeight: 600 }}>Raw judge calls ({calls.length})</div>
      {calls.map((c, i) => (
        <div key={c.id} style={{ borderTop: "1px solid var(--line-soft)", marginTop: 6, paddingTop: 4 }}>
          <div className="mut sm">
            #{i + 1} · {new Date(c.createdAt).toLocaleString()} · {c.provider}/{c.model} ·{" "}
            <span style={{ color: c.ok ? "var(--good)" : "var(--crit)" }}>{c.ok ? "ok" : `failed${c.error ? ` — ${c.error}` : ""}`}</span>
            {c.latencyMs != null ? ` · ${c.latencyMs}ms` : ""}
          </div>
          <Collapsible title="Request (system prompt + user turn, exactly as sent)" text={pretty(c.request)} />
          <Collapsible title="Response (raw body, before parsing)" text={pretty(c.response)} />
        </div>
      ))}
    </div>
  );
}

// ── make authoritative ───────────────────────────────────────────────────────

function PromoteInline({ stepRunIds, label, onDone }: { stepRunIds: string[]; label: string; onDone: () => void }): ReactNode {
  const [reason, setReason] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: async () => {
      const out: string[] = [];
      for (const id of stepRunIds) {
        const r = await api.post<{ promoted: boolean; queued: boolean }>(`/admin/ai-step-runs/${id}/promote`, { reason });
        out.push(r.queued ? "queued" : "done");
      }
      return out;
    },
    onSuccess: (out) => {
      setMsg(out.includes("queued") ? "Done — the interview evaluation goes live when the worker applies it (a few seconds)." : "Done — now live.");
      onDone();
    },
  });
  return (
    <div className="jc-promote">
      <textarea
        className="rq-in"
        style={{ minHeight: 48, fontFamily: "inherit" }}
        placeholder="Reason (required, audited) — why this result should replace the live one"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <button className="btn sm primary" disabled={reason.trim().length < 8 || m.isPending || msg !== null} onClick={() => m.mutate()}>
          {m.isPending ? "Applying…" : label}
        </button>
        <span className="mut sm">Replaces what the recruiter's report shows. Refused once a human has decided.</span>
      </div>
      {m.isError && <div className="sm" style={{ color: "var(--crit)" }}>{errText(m.error, "Couldn't make it authoritative.")}</div>}
      {msg && <div className="sm" style={{ color: "var(--good)" }}>{msg}</div>}
    </div>
  );
}

function PromoteDialog({ run, data, onClose, onDone }: { run: AiRunRow; data: PipelineResponse; onClose: () => void; onDone: () => void }): ReactNode {
  const candidates = AI_STEPS.filter(
    (s) => s.promotable === "self" && run.steps[s.key]?.status === "succeeded" && !run.steps[s.key]?.isAuthoritative && run.steps[s.key]?.stepRunId,
  );
  const [picked, setPicked] = useState<AiStepKey[]>(candidates.map((c) => c.key));
  const alignment = run.steps["transcript.align"];
  return (
    <Modal title={`Make run ${run.label} authoritative`} onClose={onClose} maxWidth={560}>
      <div className="mut sm" style={{ marginBottom: 8 }}>
        Pick the results that should replace the live ones for this candidacy ({data.candidacyState}).
      </div>
      {candidates.map((s) => (
        <label key={s.key} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
          <input
            type="checkbox"
            checked={picked.includes(s.key)}
            onChange={() => setPicked((cur) => (cur.includes(s.key) ? cur.filter((k) => k !== s.key) : [...cur, s.key]))}
          />
          <span>{s.label}</span>
          <span className="mut sm">{run.steps[s.key]?.modelLabel}</span>
        </label>
      ))}
      {picked.includes("judge.score") && alignment?.status === "succeeded" && (
        <div className="mut sm" style={{ marginBottom: 8 }}>
          If this evaluation graded this run's alignment, that aligned transcript goes live with it.
        </div>
      )}
      <PromoteInline
        stepRunIds={picked.map((k) => run.steps[k]!.stepRunId!)}
        label={`Make ${picked.length} result${picked.length === 1 ? "" : "s"} authoritative`}
        onDone={onDone}
      />
    </Modal>
  );
}

// ── org flags ────────────────────────────────────────────────────────────────

/**
 * Owner-controlled per-ORG flags (migration 0075). Read-only here: `/admin/orgs/{orgId}/flags`
 * (adminOrgDetail.ts) is the only place these are WRITTEN — this is just a pointer there.
 */
function OrgFlagsSummary({ orgId, orgName, flags }: { orgId: string; orgName: string; flags: OrgFlags }): ReactNode {
  const chip = (on: boolean, label: string): ReactNode => (
    <span className={`pill ${on ? "t" : "n"}`} style={!on ? { opacity: 0.6 } : undefined}>
      {label}: {on ? "on" : "off"}
    </span>
  );
  return (
    <div className="card pad" style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
      <span className="mut sm">{orgName}'s flags —</span>
      {chip(flags.rescoreEnabled, "Org re-evaluate")}
      {chip(flags.customPromptEnabled, "Custom prompt")}
      {chip(flags.copyQuestionsEnabled, "Copy transcript")}
      <Link to={`/admin/orgs/${orgId}`} className="btn sm ghost" style={{ marginLeft: "auto" }}>
        Manage on org page →
      </Link>
    </div>
  );
}
